/**
 * Escolha do canal do convite de senha (RN-005d): email, WhatsApp ou os dois. Canal sem dado
 * no cadastro aparece desabilitado com o motivo — o administrador vê o que falta em vez de
 * descobrir pelo erro.
 */

import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Typography,
} from "@mui/material";
import { Close, ContentCopy, Link as LinkIcon } from "@mui/icons-material";
import { colors } from "../../theme/designSystem";
import SavingOverlay from "../SavingOverlay";
import type { PasswordInviteOutcome } from "../../services/entityUserService";
import { BRAZIL_DDI, formatPhoneDisplay, legacyPhonePart, toE164Digits } from "../../utils/phone";

interface Props {
  open: boolean;
  userName: string;
  email: string | null;
  /** Já formatado para exibição ("(11) 98704-5171") ou nulo quando o cadastro não tem. */
  whatsapp: string | null;
  sending: boolean;
  /**
   * Resultado de um envio em que NENHUM canal entregou. O modal fica aberto mostrando o link
   * para copiar — é o plano B para mandar por outro meio (pedido do Hilton, 04/09).
   */
  failedOutcome: PasswordInviteOutcome | null;
  /** Texto da validade. Convite = 7 dias (padrão); "Nova senha" do perfil = 2 horas. */
  validityText?: string;
  /** Falso esconde o WhatsApp: a clínica não tem canal próprio (decisão do Hilton, 27/09/2026). */
  whatsappAvailable?: boolean;
  /**
   * Mostra "Gerar link": o convite sai sem canal e o link aparece para o administrador copiar e
   * enviar por onde quiser. Só na tela de Usuários — o "Nova senha" do perfil exige canal.
   */
  allowLinkOnly?: boolean;
  /** O resultado exibido veio de "Gerar link" (não de uma falha de envio). */
  linkGenerated?: boolean;
  onClose: () => void;
  onSend: (channels: { sendEmail: boolean; sendWhatsApp: boolean }) => void;
}

const PasswordInviteChannelModal: React.FC<Props> = ({
  open, userName, email, whatsapp, sending, failedOutcome, validityText, onClose, onSend,
  whatsappAvailable = true, allowLinkOnly = false, linkGenerated = false,
}) => {
  // Estado inicial vem das props: a tela remonta o modal por usuario (key), entao nao ha
  // efeito para sincronizar -- e nao ha janela em que os vistos aparecam desmarcados.
  const [sendEmail, setSendEmail] = useState(Boolean(email));
  const [sendWhatsApp, setSendWhatsApp] = useState(whatsappAvailable && Boolean(whatsapp));
  const [copiado, setCopiado] = useState(false);

  const copiarLink = async () => {
    if (!failedOutcome?.inviteLink) return;
    try {
      await navigator.clipboard.writeText(failedOutcome.inviteLink);
      setCopiado(true);
    } catch {
      // Sem clipboard (http, permissão negada): o campo abaixo continua selecionável.
    }
  };

  const nenhum = !sendEmail && !sendWhatsApp;

  return (
    <Dialog
      open={open}
      onClose={() => !sending && onClose()}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}
    >
      <SavingOverlay open={sending} />
      <DialogTitle
        sx={{
          backgroundColor: colors.primary,
          color: "white",
          fontWeight: 600,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Enviar convite de senha
        <IconButton
          onClick={onClose}
          disabled={sending}
          sx={{
            color: "white",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
            "&.Mui-disabled": { color: "rgba(255,255,255,0.4)" },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: "1.5rem", pt: "1.5rem !important" }}>
        <Typography sx={{ fontSize: 14, mb: 2 }}>
          Por onde <strong>{userName}</strong> recebe o link para criar a senha?
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={sendEmail}
                disabled={!email}
                onChange={(e) => setSendEmail(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Email</Typography>
                <Typography sx={{ fontSize: 12, color: email ? colors.textSecondary : "#dc3545" }}>
                  {email ?? "sem email no cadastro"}
                </Typography>
              </Box>
            }
          />
          {whatsappAvailable && (
          <FormControlLabel
            control={
              <Checkbox
                checked={sendWhatsApp}
                disabled={!whatsapp}
                onChange={(e) => setSendWhatsApp(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>WhatsApp</Typography>
                <Typography sx={{ fontSize: 12, color: whatsapp ? colors.textSecondary : "#dc3545" }}>
                  {whatsapp ?? "sem celular válido no cadastro"}
                </Typography>
              </Box>
            }
          />
          )}
        </Box>

        <Typography sx={{ fontSize: 12, color: colors.textSecondary, mt: 2 }}>
          {validityText ?? "O link é pessoal, de uso único e vale por 7 dias. Um envio novo invalida o anterior."}
        </Typography>

        {failedOutcome && (
          <Box className={linkGenerated ? "c4u-info-box" : "c4u-info-box c4u-info-box--warning"} sx={{ mt: 2, mb: 0 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                {linkGenerated ? "Link gerado." : "Nenhum canal conseguiu enviar."}
              </Typography>
              {failedOutcome.whatsappError && (
                <Typography sx={{ fontSize: 12, mb: 1 }}>WhatsApp: {failedOutcome.whatsappError}</Typography>
              )}
              <Typography sx={{ fontSize: 12, mb: 1 }}>
                {linkGenerated
                  ? "Copie e envie ao usuário pelo meio que preferir. Ele já está válido — gerar outro invalida este."
                  : "Copie o link e mande por outro meio. Ele já está válido — um envio novo o invalidaria."}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Box
                  component="input"
                  readOnly
                  value={failedOutcome.inviteLink ?? ""}
                  onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 12,
                    padding: "8px 10px",
                    border: `1px solid ${colors.border}`,
                    borderRadius: "8px",
                    fontFamily: "monospace",
                    backgroundColor: colors.white,
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ContentCopy />}
                  onClick={copiarLink}
                  sx={{ textTransform: "none", flexShrink: 0 }}
                >
                  {copiado ? "Copiado" : "Copiar"}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: "1rem 1.5rem",
          gap: 1,
          justifyContent: "flex-end",
          "& .MuiButton-root": { flexShrink: 0, minHeight: 44 },
        }}
      >
        <Button onClick={onClose} disabled={sending} variant="outlined" sx={{ textTransform: "none" }}>
          {failedOutcome ? "Fechar" : "Cancelar"}
        </Button>
        {allowLinkOnly && (
          <Button
            onClick={() => onSend({ sendEmail: false, sendWhatsApp: false })}
            disabled={sending}
            variant="outlined"
            startIcon={<LinkIcon />}
            sx={{ textTransform: "none", borderColor: colors.primary, color: colors.primary }}
          >
            Gerar link
          </Button>
        )}
        <Button
          onClick={() => onSend({ sendEmail, sendWhatsApp })}
          disabled={sending || nenhum}
          variant="contained"
          sx={{
            backgroundColor: colors.primary,
            textTransform: "none",
            "&.Mui-disabled": { backgroundColor: colors.primary, opacity: 0.7, color: "white" },
          }}
        >
          {sending ? "Enviando..." : failedOutcome ? "Tentar de novo" : "Enviar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PasswordInviteChannelModal;

/**
 * Mesma leitura do InvitePhone do servidor: fora do Brasil, DDI + número; no Brasil, DDD de 2
 * dígitos + número de 8 ou 9, descartando a parte decimal que a migração trouxe
 * ("987045171.000000"). Só para EXIBIR — quem valida é a API.
 */
export const formatWhatsApp = (
  phoneDdi?: string | null, phoneCode?: string | null, phoneNumber?: string | null,
): string | null => {
  const ddi = legacyPhonePart(phoneDdi);
  const ddd = legacyPhonePart(phoneCode);
  const numero = legacyPhonePart(phoneNumber);
  if (!numero || /^(\d)\1+$/.test(numero)) return null;
  if (ddi && ddi !== BRAZIL_DDI) {
    return toE164Digits(ddi, null, numero) ? formatPhoneDisplay(ddi, null, numero) : null;
  }
  if (ddd.length !== 2 || (numero.length !== 8 && numero.length !== 9)) return null;
  return formatPhoneDisplay(BRAZIL_DDI, ddd, numero);
};
